import { useEffect, type ChangeEvent } from "react";

type RecurrenceModalProps = {
    open: boolean;
    startsAt: string;
    endsAt: string;
    minStartsAt?: string;
    intervalWeeks: number;
    occurrences: number;
    onStartsAtChange: (value: string) => void;
    onEndsAtChange: (value: string) => void;
    onIntervalWeeksChange: (value: number) => void;
    onOccurrencesChange: (value: number) => void;
    onClose: () => void;
    onSubmit: () => void;
    isPending: boolean;
};

export default function RecurrenceModal({
    open,
    startsAt,
    endsAt,
    minStartsAt,
    intervalWeeks,
    occurrences,
    onStartsAtChange,
    onEndsAtChange,
    onIntervalWeeksChange,
    onOccurrencesChange,
    onClose,
    onSubmit,
    isPending,
}: RecurrenceModalProps) {
    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40 px-4 md:mb-0">
            <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
                <div className="flex items-center justify-between gap-4">
                    <h2 className="font-heading text-lg font-semibold font-normal bg-[#f5efe7] px-[0.775rem] py-[0.2rem] rounded-[4px]">
                        Récurrence
                    </h2>

                    <button
                        type="button"
                        onClick={onClose}
                        className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
                        aria-label="Fermer"
                    >
                        <i className="ri-close-line text-xl"></i>
                    </button>
                </div>

                <p className="mt-2 text-sm text-foreground-500">
                    Crée plusieurs séances indépendantes à partir de cette activité.
                </p>

                <div className="mt-5 space-y-4">
                    <div>
                        <label
                            htmlFor="recurrence-starts-at"
                            className="block text-sm font-medium text-foreground-700"
                        >
                            Date et heure de début
                        </label>

                        <input
                            id="recurrence-starts-at"
                            type="datetime-local"
                            min={minStartsAt}
                            value={startsAt}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                onStartsAtChange(e.target.value)
                            }
                            className="mt-1.5 w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="recurrence-ends-at"
                            className="block text-sm font-medium text-foreground-700"
                        >
                            Date et heure de fin
                        </label>

                        <input
                            id="recurrence-ends-at"
                            type="datetime-local"
                            min={startsAt || undefined}
                            value={endsAt}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                onEndsAtChange(e.target.value)
                            }
                            className="mt-1.5 w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="recurrence-interval"
                            className="block text-sm font-medium text-foreground-700"
                        >
                            Fréquence
                        </label>

                        <select
                            id="recurrence-interval"
                            value={intervalWeeks}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                onIntervalWeeksChange(Number(e.target.value))
                            }
                            className="mt-1.5 w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value={1}>Toutes les semaines</option>
                            <option value={2}>Toutes les 2 semaines</option>
                        </select>
                    </div>

                    <div>
                        <label
                            htmlFor="recurrence-occurrences"
                            className="block text-sm font-medium text-foreground-700"
                        >
                            Nombre de séances
                        </label>

                        <input
                            id="recurrence-occurrences"
                            type="number"
                            min={1}
                            max={52}
                            value={occurrences}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                onOccurrencesChange(Number(e.target.value))
                            }
                            className="mt-1.5 w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-md border border-background-300 text-foreground-700 text-sm font-semibold hover:bg-background-50 transition-colors cursor-pointer"
                    >
                        Annuler
                    </button>

                    <button
                        type="button"
                        onClick={onSubmit}
                        disabled={!startsAt || isPending}
                        className="px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isPending ? "Création..." : "Créer les séances"}
                    </button>
                </div>
            </div>
        </div>
    );
}